import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { baseurl } from '../utility/helper';
import { toLocalInput } from '../utility/instituteHelpers';
import '../assets/css/Institute.css';

const emptyForm = {
  test_name: '', test_desc: '', venue: '',
  test_start_time: '', test_end_time: '',
  register_start_date: '', register_end_date: '',
  age_group: '10-14.5', seat: 35, note: '', status: 'Active',
};

const BatchTestForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { batchId, instituteTestId } = location.state || {};
  const isEdit = !!instituteTestId;

  const [batch, setBatch] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [privilege, setPrivilege] = useState(0); // kept from the loaded test so an edit does not reset it

  useEffect(() => {
    if (batchId) {
      axios.get(`${baseurl}/admin/batches/${batchId}`)
        .then((res) => setBatch(res.data))
        .catch((err) => console.error('Error loading batch:', err));
    }
  }, [batchId]);

  useEffect(() => {
    if (!isEdit) {
      setForm(emptyForm);
      setErrors({});
      return;
    }
    axios.get(`${baseurl}/admin/tests/${instituteTestId}`)
      .then((res) => {
        const t = res.data;
        if (!batchId) {
          axios.get(`${baseurl}/admin/batches/${t.batch_id}`).then((r) => setBatch(r.data)).catch(() => {});
        }
        setPrivilege(t.under_priviledge ?? 0);
        setForm({
          test_name: t.test_title || '', test_desc: t.test_desc || '',
          venue: t.venue || '',
          test_start_time: toLocalInput(t.test_start_time),
          test_end_time: toLocalInput(t.test_end_time),
          register_start_date: toLocalInput(t.register_start_date),
          register_end_date: toLocalInput(t.register_end_date),
          age_group: t.age_group || '10-14.5', seat: t.seat ?? '',
          note: t.note || '', status: String(t.test_status).toLowerCase() === 'active' ? 'Active' : 'Inactive',
        });
      })
      .catch((err) => {
        console.error('Error loading test:', err);
        Swal.fire('Error', 'Could not load test', 'error');
      });
  }, [isEdit, instituteTestId, batchId]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const validate = () => {
    const err = {};
    if (!form.test_name.trim()) err.test_name = 'Test name is required';
    if (form.test_start_time && form.test_end_time && form.test_end_time < form.test_start_time) {
      err.test_end_time = 'End must be after start';
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const resolvedBatchId = batchId || batch?.batch_id;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const seat = form.seat === '' ? null : Number(form.seat);
    const visible = form.status === 'Active';
    const times = {
      start: form.test_start_time || null,
      end: form.test_end_time || null,
      regStart: form.register_start_date || null,
      regEnd: form.register_end_date || null,
    };

    try {
      if (isEdit) {
        // same API as the normal Test page
        await axios.put(`${baseurl}/admin/tests/${instituteTestId}`, {
          chapter_id: batch?.chapter_id ?? null,
          test_title: form.test_name, test_desc: form.test_desc, venue: form.venue,
          test_start_time: times.start, test_end_time: times.end,
          register_start_date: times.regStart, register_end_date: times.regEnd,
          age_group: form.age_group, seat, privilege,
          isVisible: visible ? 'active' : 'not-active',
        });
        Swal.fire('Updated!', 'Test updated successfully.', 'success');
      } else {
        // same API as the normal Test page, flagged as an institute (batch) test
        await axios.post(`${baseurl}/admin/tests`, {
          chapter: batch?.chapter_id ?? null,
          title: form.test_name, description: form.test_desc, venue: form.venue,
          startTime: times.start, endTime: times.end,
          regStartDateTime: times.regStart, regEndDateTime: times.regEnd,
          note: form.note, seat, ageGroup: form.age_group, privilege: 0,
          isVisible: visible ? 1 : 0,
          institute_test: 1, batch_id: resolvedBatchId,
        });
        Swal.fire('Success!', 'Test created under the batch.', 'success');
      }
      navigate('/admin/BatchDashboard', { state: { batchId: resolvedBatchId } });
    } catch (err) {
      Swal.fire('Error', err.response?.data?.error || err.response?.data?.message || 'Failed to save test', 'error');
    }
  };

  const handleDelete = async () => {
    const c = await Swal.fire({
      title: 'Delete this test?', text: form.test_name,
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#dc2626',
    });
    if (!c.isConfirmed) return;
    try {
      await axios.delete(`${baseurl}/admin/tests/${instituteTestId}`);
      Swal.fire('Deleted', 'Test cancelled.', 'success');
      navigate('/admin/BatchDashboard', { state: { batchId: resolvedBatchId } });
    } catch (err) {
      const blocked = err.response?.status === 400;
      Swal.fire(blocked ? 'Cannot Delete' : 'Error', err.response?.data?.message || 'Failed to delete test', blocked ? 'warning' : 'error');
    }
  };

  const batchNotActive = batch && batch.status !== 'Active';

  return (
    <div className="content container-fluid inst-wrap">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
        <h3 className="mb-0"><b>{isEdit ? 'Edit Test' : 'Add Test'}</b></h3>
      </div>

      {batch && (
        <div className="inst-card">
          <b>{batch.institute_name}</b> · Batch {batch.batch_name} ({batch.batch_code}) · Status: {batch.status}
          {batchNotActive && !isEdit && (
            <div className="text-danger mt-2">
              This batch is not Active — tests can only be created under an Active batch.
            </div>
          )}
        </div>
      )}

      <div className="inst-card">
        <form onSubmit={handleSubmit} className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Test Name</label>
            <input className="form-control" name="test_name" value={form.test_name} onChange={handleChange} />
            {errors.test_name && <small className="text-danger">{errors.test_name}</small>}
          </div>
          <div className="col-md-6">
            <label className="form-label">Venue</label>
            <input className="form-control" name="venue" value={form.venue} onChange={handleChange} />
          </div>
         
          <div className="col-md-6">
            <label className="form-label">Registration Start</label>
            <input type="datetime-local" className="form-control" name="register_start_date"
              value={form.register_start_date} onChange={handleChange} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Registration End</label>
            <input type="datetime-local" className="form-control" name="register_end_date"
              value={form.register_end_date} onChange={handleChange} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Test Start</label>
            <input type="datetime-local" className="form-control" name="test_start_time"
              value={form.test_start_time} onChange={handleChange} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Test End</label>
            <input type="datetime-local" className="form-control" name="test_end_time"
              value={form.test_end_time} onChange={handleChange} />
            {errors.test_end_time && <small className="text-danger">{errors.test_end_time}</small>}
          </div>
           <div className="col-md-6">
            <label className="form-label">Age Group</label>
            <select className="form-select" name="age_group" value={form.age_group} onChange={handleChange}>
              <option value="10-14.5">10 to 14.5 years</option>
              <option value="14.5-99">14.5 to 99 years</option>
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label">Seat Capacity</label>
            <input type="number" min="1" className="form-control" name="seat" value={form.seat} onChange={handleChange} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Status</label>
            <select className="form-select" name="status" value={form.status} onChange={handleChange}>
              <option value="Draft">Draft</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label">Description</label>
            <textarea className="form-control" name="test_desc" value={form.test_desc} onChange={handleChange} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Note</label>
            <textarea className="form-control" name="note" value={form.note} onChange={handleChange} />
          </div>

          <div className="col-12 text-center">
            <Link to="/admin/BatchDashboard" state={{ batchId: resolvedBatchId }}>
              <button type="button" className="btn btn-secondary">Cancel</button>
            </Link>
            &nbsp;&nbsp;
            <button type="submit" className="btn btn-primary" style={{ borderRadius: 8 }}
              disabled={batchNotActive && !isEdit}>
              {isEdit ? 'Update Test' : 'Create Test'}
            </button>
            {isEdit && (
              <>
                &nbsp;&nbsp;
                <button type="button" className="btn btn-danger" onClick={handleDelete}>
                  <i className="fas fa-trash" /> Delete Test
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default BatchTestForm;
